import { Col, Row, Typography } from 'antd'
import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Input/Input'
import { Modal } from 'antd'
import { Select } from 'common/components/Select/Select'
import { Table } from 'common/components/Table/Table'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import { DEFAULT_TABLE_PARAMS_WITH_SEARCH } from 'common/constants/defaultTableParamsConstants'
import type { TableParamsType } from 'common/interfaces/ITable'
import dayjs from 'dayjs'
import type { IPatient } from 'features/Patients/interfaces/IPatient'
import { useLazyFetchPatientsQuery } from 'features/Patients/state/api/patientApi'
import { patientsTableColumns } from 'features/Schedule/pages/components/ManageAppointment/PatientSearchModal/columns'
import { useCallback, useEffect, useRef, useState, type FC } from 'react'
import { type EncounterData, useLazyFetchPatientEncountersQuery } from './transactionsMapping.api'
import styles from './transactionsMapping.module.scss'

const { Text } = Typography

type Props = {
  isOpen: boolean
  onClose: () => void
  onSelectPatient: (patient: IPatient, encounterEid: number) => void
  row?: any
  type?: string
}

const PatientsTableParams = {
  ...DEFAULT_TABLE_PARAMS_WITH_SEARCH,
  pageSize: 10,
}

export const ResolveTransactionModal: FC<Props> = ({
  isOpen,
  onClose,
  onSelectPatient,
  row,
  type,
}) => {
  const [tableParams, setTableParams] = useState<TableParamsType>(PatientsTableParams)
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedPatient, setSelectedPatient] = useState<IPatient | null>(null)
  const [encounters, setEncounters] = useState<EncounterData[]>([])
  const [selectedEncounter, setSelectedEncounter] = useState<number | null>(null)
  const [fetchEncounters, { data: encountersData, isFetching: isLoadingEncounters }] =
    useLazyFetchPatientEncountersQuery()
  const searchInputRef = useRef<any>(null)
  const [
    fetchPatientsData,
    { data: PatientsData, isFetching: isLoadingPatientsData, reset: resetPatientsData },
  ] = useLazyFetchPatientsQuery()

  const handleRowClick = useCallback((patient: IPatient) => {
    setSelectedPatient(patient)
    setSelectedEncounter(null)
    setEncounters([])
    if (patient?.id) {
      fetchEncounters({ patientId: patient.id })
    }
  }, [])

  const handleSearch = useCallback(() => {
    if (searchValue.length >= 2) {
      setTableParams({
        ...tableParams,
        debouncedValue: searchValue,
      })
    }
  }, [searchValue])

  useEffect(() => {
    if (isOpen && type === 'transactions_with_known_patient' && row?.patient) {
      setSelectedPatient(row.patient)
      setSelectedEncounter(null)
      setEncounters([])
      if (row.patient.id) {
        fetchEncounters({ patientId: row.patient.id })
      }
    }
    if (!isOpen) {
      setSelectedPatient(null)
      setSelectedEncounter(null)
      setEncounters([])
    }
  }, [isOpen, type, row])

  useEffect(() => {
    if (tableParams.debouncedValue?.length >= 2) {
      fetchPatientsData({
        ...tableParams,
        search: tableParams.debouncedValue,
      })
    }
  }, [tableParams.pageSize, tableParams.current, tableParams.debouncedValue, tableParams.sorters])

  useEffect(() => {
    if (Number.isInteger(PatientsData?.total_items))
      setTableParams({ ...tableParams, total: PatientsData.total_items })
  }, [PatientsData])

  useEffect(() => {
    if (encountersData?.encounters?.data) {
      setEncounters(encountersData?.encounters?.data)
    }
  }, [encountersData])

  useEffect(() => {
    if (isOpen && searchInputRef.current && type !== 'transactions_with_known_patient') {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, type])

  useEffect(() => {
    if (!isOpen) {
      setSelectedPatient(null)
      setSelectedEncounter(null)
      setEncounters([])
      setSearchValue('')
      setTableParams(PatientsTableParams)

      if (typeof resetPatientsData === 'function') {
        resetPatientsData()
      }
    }
  }, [isOpen])

  const handleSubmit = () => {
    if (selectedPatient && selectedEncounter) {
      onSelectPatient(selectedPatient, selectedEncounter)
      onClose()
    }
  }

  return (
    <Modal
      classNames={{
        content: styles.resolveKnownPatientModal,
      }}
      destroyOnHidden
      title={
        type === 'transactions_with_known_patient'
          ? 'Resolve Transaction for Known Patient'
          : 'Resolve Transaction for New Patient'
      }
      open={isOpen}
      onCancel={onClose}
      width='1400px'
      footer={null}>
      {type === 'transactions_with_known_patient' && selectedPatient && (
        <Select
          label='Select Encounter'
          isLoading={isLoadingEncounters}
          listOptions={(Array.isArray(encounters) ? encounters : []).map((e) => ({
            code: e.eid,
            name: `${dayjs(e.date).format('MM-DD-YYYY')} - ${e.pc_catname}`,
          }))}
          value={selectedEncounter ?? undefined}
          onChange={(val) => setSelectedEncounter(val)}
          placeholder='Select an encounter'
          required
        />
      )}
      {type !== 'transactions_with_known_patient' && (
        <>
          <div className={styles.searchContainer}>
            <Row gutter={[16, 8]} align='middle' justify={{ xs: 'space-between', sm: 'start' }}>
              <Col>
                <Text className={styles.searchLabel} strong>
                  Search for:
                </Text>
              </Col>
              <Col flex='auto' xs={{ order: 3 }} sm={{ order: 2 }}>
                <Input
                  ref={searchInputRef}
                  allowClear
                  name='search'
                  placeholder='First name, last name, phone number'
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch()
                    }
                  }}
                />
              </Col>
              <Col xs={{ order: 2 }} sm={{ order: 3 }}>
                <Button onClick={handleSearch} disabled={searchValue.trim().length < 2}>
                  Search
                </Button>
              </Col>
            </Row>
          </div>

          {PatientsData?.items && (
            <div className={styles.recordsFound}>
              <Text strong>{PatientsData.total_items} records found.</Text>
            </div>
          )}

          <Table
            onClickRow={handleRowClick}
            isLoading={isLoadingPatientsData}
            itemKey='id'
            scroll={{ x: 'max-content' }}
            tableParams={tableParams}
            dataSource={PatientsData?.items}
            setTableParams={setTableParams}
            columns={patientsTableColumns}
            haveActions={false}
            rowClassName={(row: IPatient) =>
              selectedPatient && row.id === selectedPatient.id ? styles.selectedRow : ''
            }
          />
          {selectedPatient && (
            <div style={{ margin: '16px 0' }}>
              <Select
                label='Select Encounter'
                isLoading={isLoadingEncounters}
                listOptions={(Array.isArray(encounters) ? encounters : []).map((e) => ({
                  code: e.eid,
                  name: e.reason
                    ? `${dayjs(e.date).format('MM-DD-YYYY')} - ${e.reason}`
                    : dayjs(e.date).format('MM-DD-YYYY'),
                }))}
                value={selectedEncounter ?? undefined}
                onChange={(val) => setSelectedEncounter(val)}
                placeholder='Select an encounter'
                required
              />
            </div>
          )}
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
        <Button onClick={onClose} modifier={BUTTON_MODIFIER.SECONDARY}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!selectedPatient || !selectedEncounter}
          modifier={BUTTON_MODIFIER.PRIMARY}>
          Save
        </Button>
      </div>
    </Modal>
  )
}
